import { activityProjectId } from "./activityProject";
import {
  useGetMany,
  CanAccess,
  useListContext,
  useInfinitePaginationContext,
  useTranslate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/admin/spinner";
import { RotateCcw } from "lucide-react";
import type { Activity } from "../types";
import { InfinitePagination } from "../misc/InfinitePagination";
import { useIsMobile } from "@/hooks/use-mobile";
import { ActivityEntry } from "./ActivityEntry";
export function ActivityLogIterator() {
  const isMobile = useIsMobile();
  const { data, isPending, error, refetch } = useListContext<Activity>();
  const { hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfinitePaginationContext();
  const translate = useTranslate();
  const ids = [
    ...new Set(
      (data ?? []).flatMap((a) => {
        const id = activityProjectId(a);
        return id == null ? [] : [id];
      }),
    ),
  ];
  const { data: projects = [] } = useGetMany(
    "project_lookup",
    { ids },
    { enabled: ids.length > 0 },
  );
  const projectColors = new Map(
    projects.map((p) => [String(p.id), p.calendar_color as string]),
  );

  if (isPending) {
    return (
      <div className="mt-1">
        {Array.from({ length: 5 }).map((_, index) => (
          <div className="space-y-2 mt-1" key={index}>
            <div className="flex flex-row space-x-2 items-center">
              <Skeleton className="w-5 h-5 rounded-full" />
              <Skeleton className="w-full h-4" />
            </div>
            <Skeleton className="w-full h-12" />
            <Separator />
          </div>
        ))}
      </div>
    );
  }

  if (error && !data?.length) {
    return (
      <div className="p-4">
        <div className="text-center text-muted-foreground mb-4">
          {translate("crm.dashboard.latest_activity_error", {
            _: "Error loading latest activity",
          })}
        </div>
        <div className="text-center mt-2">
          <Button onClick={() => refetch()}>
            <RotateCcw />
            {translate("crm.common.retry")}
          </Button>
        </div>
      </div>
    );
  }

  if (!data?.length)
    return (
      <p className="rounded-xl border p-5 text-sm text-muted-foreground">
        Событий по выбранным фильтрам нет
      </p>
    );

  return (
    <div className="space-y-4">
      {data?.map((activity) => (
        <ActivityItem
          key={activity.id}
          activity={activity}
          color={projectColors.get(String(activityProjectId(activity)))}
        />
      ))}

      {/* Desktop: explicit Load More button */}
      {!isMobile && hasNextPage && (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            fetchNextPage();
          }}
          className="flex w-full justify-center text-sm underline hover:no-underline"
        >
          {isFetchingNextPage ? (
            <Spinner />
          ) : (
            translate("crm.activity.load_more")
          )}
        </a>
      )}

      {/* Mobile: auto-load on scroll via IntersectionObserver */}
      {isMobile && (
        <div className="flex justify-center">
          <InfinitePagination />
        </div>
      )}
    </div>
  );
}

function ActivityItem({
  activity,
  color,
}: {
  activity: Activity;
  color?: string;
}) {
  const entry = (
    <div data-motion-item>
      <ActivityEntry activity={activity} color={color} />
    </div>
  );
  return activity.type === "deal.created" ||
    activity.type === "dealNote.created" ? (
    <CanAccess resource="deals" action="list">
      {entry}
    </CanAccess>
  ) : (
    entry
  );
}
